/**
 * Export AI Activity Bot trade-history rows as CSV for spreadsheet analysis.
 */

import type {
  ActivityBotPositionHistoryRow,
  CryptoPortfolioActivityBot,
} from '../services/cryptoPortfolioService';

const CSV_HEADERS = [
  'botId',
  'botName',
  'sectorId',
  'sectorLabel',
  'filterProfileName',
  'hedgePositionId',
  'symbol',
  'side',
  'status',
  'openedAt',
  'closedAt',
  'entryPrice',
  'exitPrice',
  'currentPrice',
  'sizeUsd',
  'leverage',
  'realizedPnl',
  'unrealizedPnl',
  'pnl',
  'pnlOnSizePct',
] as const;

function csvEscape(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function safeFileToken(raw: string | null | undefined, fallback: string): string {
  return (raw || fallback)
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || fallback;
}

function effectivePnl(row: ActivityBotPositionHistoryRow): number | null {
  const isOpen = row.status === 'OPEN';
  const pnl = isOpen ? row.unrealizedPnl : row.realizedPnl;
  if (pnl == null) {
    return null;
  }
  const n = Number(pnl);
  return Number.isFinite(n) ? n : null;
}

function rowToCsvCells(
  bot: CryptoPortfolioActivityBot,
  row: ActivityBotPositionHistoryRow,
): string[] {
  const pnl = effectivePnl(row);
  return [
    csvEscape(bot.id),
    csvEscape(bot.referenceName || bot.displayLabel || `bot-${bot.id}`),
    csvEscape(bot.sectorId),
    csvEscape(bot.sectorLabel ?? ''),
    csvEscape(bot.filterProfileName ?? ''),
    csvEscape(row.hedgePositionId ?? ''),
    csvEscape(row.symbol),
    csvEscape(row.side),
    csvEscape(row.status),
    csvEscape(row.openedAt ?? ''),
    csvEscape(row.closedAt ?? ''),
    csvEscape(row.entryPrice ?? ''),
    csvEscape(row.exitPrice ?? ''),
    csvEscape(row.currentPrice ?? ''),
    csvEscape(row.sizeUsd ?? ''),
    csvEscape(row.leverage ?? ''),
    csvEscape(row.realizedPnl ?? ''),
    csvEscape(row.unrealizedPnl ?? ''),
    csvEscape(pnl ?? ''),
    csvEscape(row.pnlOnSizePct ?? ''),
  ];
}

export function buildActivityBotHistoryCsv(
  bot: CryptoPortfolioActivityBot,
  rows: ActivityBotPositionHistoryRow[],
): string {
  const lines = [CSV_HEADERS.join(',')];
  for (const row of rows) {
    if (!row) continue;
    lines.push(rowToCsvCells(bot, row).join(','));
  }
  return lines.join('\r\n');
}

export function buildAllActivityBotsHistoryCsv(
  entries: Array<{ bot: CryptoPortfolioActivityBot; rows: ActivityBotPositionHistoryRow[] }>,
): string {
  const lines = [CSV_HEADERS.join(',')];
  for (const entry of entries) {
    if (!entry?.bot) continue;
    for (const row of entry.rows ?? []) {
      if (!row) continue;
      lines.push(rowToCsvCells(entry.bot, row).join(','));
    }
  }
  return lines.join('\r\n');
}

function triggerCsvDownload(filename: string, csv: string): void {
  // BOM helps Excel detect UTF-8
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function downloadActivityBotHistoryCsv(
  bot: CryptoPortfolioActivityBot,
  rows: ActivityBotPositionHistoryRow[],
): void {
  const stamp = new Date().toISOString().slice(0, 10);
  const name = safeFileToken(bot.referenceName || bot.displayLabel, `bot-${bot.id}`);
  const csv = buildActivityBotHistoryCsv(bot, rows);
  triggerCsvDownload(`activity-bot-history-${name}-${stamp}.csv`, csv);
}

export function downloadAllActivityBotsHistoryCsv(
  entries: Array<{ bot: CryptoPortfolioActivityBot; rows: ActivityBotPositionHistoryRow[] }>,
): void {
  const stamp = new Date().toISOString().slice(0, 10);
  const csv = buildAllActivityBotsHistoryCsv(entries);
  triggerCsvDownload(`activity-bots-history-all-${stamp}.csv`, csv);
}
